import React from "react";
import { Box, Typography } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import theme from "../../theme";
import { TRACKING_STEPS, formatStepDateTime } from "./orderTracking";

const TRACK_GRAY = "#dadce0";
const LABEL_ACTIVE = "#202124";
const LABEL_INACTIVE = "#5f6368";
const LABEL_DYNAMIC = "#FF8500";

/**
 * Horizontal order status stepper (Created → Approved → Paid → Packed → Shipped → Delivered).
 */
export default function OrderTrackingStepper({
  lastCompletedIndex = -1,
  stageTimestamps = {},
  stepLabels = {},
}) {
  const blue = theme.palette.custom.primary;
  const stepCount = TRACKING_STEPS.length;
  const safeLast = Math.min(
    Math.max(lastCompletedIndex, -1),
    stepCount - 1
  );

  const edgeInset = `${100 / (stepCount * 2)}%`;
  const trackSpan = `calc(100% - ${100 / stepCount}%)`;
  const progressRatio =
    safeLast >= 0 && stepCount > 1 ? safeLast / (stepCount - 1) : 0;

  return (
    <Box sx={{ width: "100%", pt: 1, pb: 0.5 }}>
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "flex-start",
          width: "100%",
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: edgeInset,
            width: trackSpan,
            top: 17,
            height: 3,
            bgcolor: TRACK_GRAY,
            borderRadius: 2,
            zIndex: 0,
          }}
        />
        {safeLast >= 0 && progressRatio > 0 && (
          <Box
            aria-hidden
            sx={{
              position: "absolute",
              left: edgeInset,
              width: `calc(${trackSpan} * ${progressRatio})`,
              top: 17,
              height: 3,
              bgcolor: blue,
              borderRadius: 2,
              zIndex: 1,
              transition: "width 0.35s ease",
            }}
          />
        )}

        {TRACKING_STEPS.map((step, index) => {
          const done = index <= safeLast;
          const stepAt = formatStepDateTime(stageTimestamps[step.key]);
          const dynamicLabel = stepLabels[step.key];
          const label = dynamicLabel || step.label;
          const isDynamicPending = Boolean(dynamicLabel && !done);
          return (
            <Box
              key={step.key}
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                zIndex: 2,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                  bgcolor: done ? blue : "#fff",
                  border: done ? "none" : `2px solid ${TRACK_GRAY}`,
                  boxSizing: "border-box",
                }}
              >
                {done ? (
                  <CheckIcon sx={{ fontSize: 22, color: "#fff", fontWeight: 700 }} />
                ) : null}
              </Box>
              <Typography
                component="span"
                sx={{
                  mt: 1.25,
                  fontSize: { xs: 11, sm: 13 },
                  fontWeight: done || isDynamicPending ? 600 : 400,
                  color: done
                    ? LABEL_ACTIVE
                    : isDynamicPending
                      ? LABEL_DYNAMIC
                      : LABEL_INACTIVE,
                  textAlign: "center",
                  lineHeight: 1.35,
                  px: { xs: 0.25, sm: 0.5 },
                  display: "block",
                  width: "100%",
                }}
              >
                {label}
              </Typography>
              {done ? (
                <Typography
                  component="span"
                  sx={{
                    mt: 0.5,
                    fontSize: { xs: 10, sm: 11 },
                    fontWeight: 400,
                    color: LABEL_INACTIVE,
                    textAlign: "center",
                    lineHeight: 1.3,
                    px: { xs: 0.25, sm: 0.5 },
                    display: "block",
                    width: "100%",
                    whiteSpace: "nowrap",
                  }}
                >
                  {stepAt || "—"}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
